import axiosInstance from "../axiosInstance";
import {
  KpiCard,
  TransactionsResponse,
  VolumePoint,
  PatternPoint,
  YearOnYear,
} from "../../types/transactions";

function getTenantId(): string | null {
  try {
    const u = localStorage.getItem("user_info");
    if (!u) return null;
    const parsed = JSON.parse(u);
    return parsed.org_id || parsed.org_guid || null;
  } catch {
    return null;
  }
}

const BASE = "/api/analytics";

// Simple in-memory dedupe + short TTL cache to avoid duplicate network calls
const _inFlight = new Map<string, Promise<any>>();
const _cache = new Map<string, { expires: number; data: any }>();

function requestWithDedupe<T>(
  key: string,
  fn: () => Promise<T>,
  ttlMs = 5000,
): Promise<T> {
  const now = Date.now();
  const cached = _cache.get(key);
  if (cached && cached.expires > now) {
    return Promise.resolve(cached.data as T);
  }
  const inflight = _inFlight.get(key);
  if (inflight) return inflight as Promise<T>;
  const p = fn()
    .then((res) => {
      _cache.set(key, { expires: now + ttlMs, data: res });
      _inFlight.delete(key);
      return res;
    })
    .catch((err) => {
      _inFlight.delete(key);
      throw err;
    });
  _inFlight.set(key, p);
  return p;
}

const ANALYTICS_TIMEOUT = 30000;

export async function getTransactionKpis(
  start_date?: string,
  end_date?: string,
): Promise<KpiCard[]> {
  const tenant = getTenantId();
  const url = tenant
    ? `${BASE}/tenants/${tenant}/analytics/transaction-kpis`
    : `${BASE}/transaction-kpis`;
  const params = { start_date, end_date };
  const key = `getTransactionKpis:${url}?${JSON.stringify(params)}`;
  return requestWithDedupe(key, async () => {
    const res = await axiosInstance.get<{ cards: KpiCard[] }>(url, {
      params,
      timeout: ANALYTICS_TIMEOUT,
    });
    return res.data.cards;
  });
}

export async function getTransactions(
  params: Record<string, unknown> = {},
): Promise<TransactionsResponse> {
  const tenant = getTenantId();
  const url = tenant
    ? `${BASE}/tenants/${tenant}/analytics/transactions`
    : `${BASE}/transactions`;
  const key = `getTransactions:${url}?${JSON.stringify(params)}`;
  return requestWithDedupe(key, async () => {
    const res = await axiosInstance.get<TransactionsResponse>(url, {
      params,
      timeout: ANALYTICS_TIMEOUT,
    });
    return res.data;
  });
}

export function getTransactionsExportUrl(
  params: Record<string, unknown> = {},
): string {
  const tenant = getTenantId();
  const url = tenant
    ? `${BASE}/tenants/${tenant}/analytics/transactions/export`
    : `${BASE}/transactions/export`;
  const qp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null) qp.append(k, String(v));
  });
  return `${axiosInstance.defaults.baseURL?.replace(/\/$/, "") || ""}${url}?${qp.toString()}`;
}

export async function getVolume(
  params: Record<string, unknown> = {},
): Promise<VolumePoint[]> {
  const tenant = getTenantId();
  const url = tenant
    ? `${BASE}/tenants/${tenant}/analytics/transactions/volume`
    : `${BASE}/transactions/volume`;
  const key = `getVolume:${url}?${JSON.stringify(params)}`;
  return requestWithDedupe(key, async () => {
    const res = await axiosInstance.get<VolumePoint[]>(url, {
      params,
      timeout: ANALYTICS_TIMEOUT,
    });
    return res.data;
  });
}

export async function getPatterns(
  params: Record<string, unknown> = {},
): Promise<PatternPoint[]> {
  const tenant = getTenantId();
  const url = tenant
    ? `${BASE}/tenants/${tenant}/analytics/transactions/patterns`
    : `${BASE}/transactions/patterns`;
  const key = `getPatterns:${url}?${JSON.stringify(params)}`;
  return requestWithDedupe(key, async () => {
    const res = await axiosInstance.get<PatternPoint[]>(url, {
      params,
      timeout: ANALYTICS_TIMEOUT,
    });
    return res.data;
  });
}

export async function getYearOnYear(year?: number): Promise<YearOnYear> {
  const tenant = getTenantId();
  const url = tenant
    ? `${BASE}/tenants/${tenant}/analytics/transactions/year-on-year`
    : `${BASE}/transactions/year-on-year`;
  const params = { year };
  const key = `getYearOnYear:${url}?${JSON.stringify(params)}`;
  return requestWithDedupe(key, async () => {
    const res = await axiosInstance.get<YearOnYear>(url, { params });
    return res.data;
  });
}

export default {
  getTransactionKpis,
  getTransactions,
  getTransactionsExportUrl,
  getVolume,
  getPatterns,
  getYearOnYear,
};
